/**
 * Governance findings (docs/domain/PROCUREMENT_DOMAIN.md §5). Closed list: extend only via
 * code review, together with the domain doc. Raised by the conversation turn (WP-2.1) and
 * referenced by eval cases (WP-1.11).
 */
export const FINDING_TYPES = [
  "BUDGET_NOT_APPROVED",
  "PRICE_JUSTIFICATION_MISSING",
  "PRICE_NO_LONGER_VALID",
  "DELIVERABLE_EXCEEDS_QUOTE",
  "UNCONFIRMED_SUPPLIER_ASSERTION",
  "REQUESTER_SUPERSEDES_DOCUMENT",
  "PENDING_SUPPLIER_DOC_REVISION",
  "PAYMENT_BEFORE_ACCEPTANCE",
  "LAPSED_MILESTONE",
  "OFFER_VALIDITY_EXPIRED",
  "IMMATERIAL_DISCOUNT",
  "SOLE_SOURCE_NO_ALTERNATIVES",
  "CAPACITY_ONLY_JUSTIFICATION",
  "UNIQUE_EXPERTISE_LOCK_IN",
  "LONG_EMBEDDED_INDIVIDUAL",
  "MULTI_ENTITY_RACI_REQUIRED",
  "UNRESOLVED_CONTRADICTION",
  "IMPACT_NOT_QUANTIFIED",
  "ACCEPTANCE_BROADER_THAN_DELIVERABLE",
  "PERSONAL_DATA_PROCESSING",
  /** Deterministic: acceptance criteria only point to a framework/tracker (legacy N30). */
  "ACCEPTANCE_NOT_VERIFIABLE",
  /** Deterministic: a narrative field was captured from a passing remark (legacy E18). */
  "NARRATIVE_NOT_SUBSTANTIATED",
  /** Deterministic: a value was derived or recorded by a rule, not stated by the requester. */
  "SYSTEM_NOTE",
  "OTHER",
] as const;
export type FindingType = (typeof FINDING_TYPES)[number];

/** Raised only by deterministic rules in code, never by the model. */
export const SYSTEM_FINDING_TYPES = [
  "ACCEPTANCE_NOT_VERIFIABLE",
  "NARRATIVE_NOT_SUBSTANTIATED",
  "SYSTEM_NOTE",
] as const;
/** What the conversation model may raise (TurnOutput). */
export const MODEL_FINDING_TYPES = FINDING_TYPES.filter(
  (t): t is Exclude<FindingType, (typeof SYSTEM_FINDING_TYPES)[number]> =>
    !(SYSTEM_FINDING_TYPES as readonly string[]).includes(t),
) as [
  Exclude<FindingType, (typeof SYSTEM_FINDING_TYPES)[number]>,
  ...Exclude<FindingType, (typeof SYSTEM_FINDING_TYPES)[number]>[],
];

export const FINDING_SEVERITIES = ["INFO", "WARN", "HIGH"] as const;
export type FindingSeverity = (typeof FINDING_SEVERITIES)[number];

/**
 * "Before you send" (WP-2.8, ADR-0030): the finding types a requester can act on themselves — each
 * with the field that settles it, or null when the conversation does. Every other type is an
 * internal assessment for procurement (supplier, sourcing and commercial judgement) and never
 * reaches a requester payload; nor do SYSTEM_NOTE and closed findings.
 */
export const REQUESTER_FINDING_FIELDS = {
  BUDGET_NOT_APPROVED: "budget_approval_status",
  PRICE_JUSTIFICATION_MISSING: "budget_estimate_basis",
  MULTI_ENTITY_RACI_REQUIRED: "raci_defined",
  IMPACT_NOT_QUANTIFIED: "risk_of_not_doing",
  NARRATIVE_NOT_SUBSTANTIATED: "risk_of_not_doing",
  ACCEPTANCE_BROADER_THAN_DELIVERABLE: "acceptance_criteria",
  ACCEPTANCE_NOT_VERIFIABLE: "acceptance_criteria",
  LAPSED_MILESTONE: "target_go_live",
  UNRESOLVED_CONTRADICTION: null,
} as const satisfies Partial<Record<FindingType, string | null>>;
export type RequesterFindingType = keyof typeof REQUESTER_FINDING_FIELDS;

export function isRequesterFinding(type: FindingType): type is RequesterFindingType {
  return Object.hasOwn(REQUESTER_FINDING_FIELDS, type);
}
